import React, {useMemo, useEffect} from 'react';
import {ProfileData} from '../../../types';
import classNames from 'classnames';
import Icon from '../../icon';
import {QRCode} from '../../qr';
import {TypeNumber} from 'qr-code-styling';

import {
  SectionHeader,
  SkillItem,
  PortfolioItem,
  ExperienceItem,
  EducationItem,
} from './components';

interface ResumeComponentProps {
  resumeData: ProfileData;
  isForPDF?: boolean;
  qrData?: string;
  onImageLoad?: (src: string) => void;
  onDOMStable?: () => void;
}

export function ResumeComponent({
  resumeData,
  isForPDF = false,
  qrData,
  onImageLoad,
  onDOMStable,
}: ResumeComponentProps) {
  // Memoize QR code configuration to prevent unnecessary re-renders
  const qrConfig = useMemo(
    () => ({
      width: 70,
      height: 70,
      margin: 0,
      type: 'canvas' as const,
      qrOptions: {
        typeNumber: 0 as TypeNumber,
        mode: 'Byte' as const,
        errorCorrectionLevel: 'L' as const,
      },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.4,
        margin: 0,
      },
      dotsOptions: {
        type: 'rounded' as const,
        color: '#FF5975',
        gradient: {
          type: 'linear' as const,
          rotation: 0,
          colorStops: [
            {offset: 0, color: '#FF5975'},
            {offset: 1, color: '#FFB858'},
          ],
        },
      },
      backgroundOptions: {
        color: 'transparent',
      },
      cornersSquareOptions: {
        type: 'extra-rounded' as const,
        color: '#FF5975',
      },
      cornersDotOptions: {
        type: 'dot' as const,
        color: '#FF5975',
      },
    }),
    [],
  );

  // Normalize availability data (handles object or array shape)
  const availabilityInfo = useMemo(() => {
    const raw: unknown = (resumeData as unknown as {availability?: unknown})
      .availability;
    if (!raw) return null;
    const item = Array.isArray(raw) ? raw[0] : (raw as unknown);
    if (!item) return null;

    const isAvailable: boolean | undefined = (
      item as unknown as {availability?: boolean}
    ).availability;
    const min: number | null | undefined = (
      item as unknown as {hoursPerWeekMin?: number}
    ).hoursPerWeekMin;
    const max: number | null | undefined = (
      item as unknown as {hoursPerWeekMax?: number}
    ).hoursPerWeekMax;

    let hoursLabel: string | null = null;
    if (min != null && max != null) {
      hoursLabel = `${min}-${max}`;
    } else if (min != null) {
      hoursLabel = `${min}`;
    } else if (max != null) {
      hoursLabel = `${max}`;
    }

    return {
      isAvailable,
      hoursLabel,
    } as {isAvailable?: boolean; hoursLabel: string | null};
  }, [resumeData]);

  const showAvailability = Boolean(
    availabilityInfo && typeof availabilityInfo.isAvailable !== 'undefined',
  );

  // Notify parent when component is fully rendered
  useEffect(() => {
    if (onDOMStable) {
      let checkCount = 0;
      let lastHash = '';

      // Wait for React to finish rendering and then check DOM stability
      const checkDOMStability = () => {
        // Wait for React to finish rendering using requestAnimationFrame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Look for specific content that indicates the page is fully rendered
            const resumeContent = document.querySelector(
              '.flex.flex-col.w-full.bg-white',
            ); // Main resume container
            const avatarImage = document.querySelector('img[alt="Profile"]'); // Profile avatar image
            const qrCode = document.querySelectorAll('.qr-code-container'); // QR code component

            // Check if key elements are present
            const hasResumeContent = !!resumeContent;
            const hasAvatar = !!avatarImage;
            const hasQRCode = qrCode.length > 0;

            // Get the main content area for hash checking
            const mainContent = resumeContent || document.body;
            const content = mainContent.innerHTML.replace(
              /data-print-ready="[^"]*"/g,
              '',
            );

            // Create a simple content fingerprint that works with any characters
            const createContentFingerprint = (str: string) => {
              const length = str.length;
              const charSum = str
                .split('')
                .reduce((sum, char) => sum + char.charCodeAt(0), 0);
              const firstChars = str.slice(0, 200).replace(/[^a-zA-Z0-9]/g, ''); // First 200 alphanumeric chars
              return `${length}-${charSum}-${firstChars.length}`;
            };

            const domHash = createContentFingerprint(content);

            console.log('🔍 DOM stability check:', {
              checkCount: checkCount + 1,
              hasResumeContent,
              hasAvatar,
              hasQRCode,
              lastHash: lastHash,
              currentHash: domHash,
              isStable: lastHash === domHash,
            });

            // DOM is stable if:
            // 1. Key content elements are present
            // 2. Fingerprint hasn't changed (no DOM updates)
            // 3. We've done at least 2 checks to ensure stability
            if (hasResumeContent && lastHash === domHash && checkCount >= 1) {
              onDOMStable();
            } else {
              // DOM is still changing or key elements missing
              lastHash = domHash;
              checkCount++;

              // Continue checking if we haven't reached max attempts
              if (checkCount < 20) {
                console.log('⏳ DOM not stable yet - retrying...');
                setTimeout(checkDOMStability, 100);
              } else {
                console.log(
                  '⚠️ DOM stability timeout reached, proceeding anyway',
                );
                onDOMStable();
              }
            }
          });
        });
      };

      // Start DOM stability check after a small delay
      const timer = setTimeout(checkDOMStability, 100);
      return () => clearTimeout(timer);
    }
  }, [onDOMStable, resumeData]);

  return (
    <div className="flex flex-col w-full bg-white shadow-lg overflow-hidden font-['Nunito_Sans'] print:shadow-none">
      {/* Header Section */}
      <div className="px-10 py-8 text-white flex items-center gap-5 bg-gradient-to-br from-[#F24E1E] to-[#FF7816]">
        <div className="relative flex-shrink-0">
          <div className="w-48 h-48 rounded-full border-4 border-white overflow-hidden shadow-lg">
            {resumeData.avatar ? (
              <img
                src={resumeData.avatar}
                alt="Profile"
                className="w-full h-full object-cover"
                onLoad={() => {
                  resumeData.avatar && onImageLoad?.(resumeData.avatar);
                }}
                onError={e => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.onerror = null;
                  target.style.display = 'none';
                  const fallback = target.parentElement?.querySelector(
                    '[data-fallback-avatar]',
                  ) as HTMLElement | null;
                  if (fallback) fallback.style.display = 'flex';
                  // Even if image fails to load, count it as loaded
                  resumeData.avatar && onImageLoad?.(resumeData.avatar);
                }}
              />
            ) : null}
            <div
              data-fallback-avatar
              className={classNames(
                'absolute inset-0 items-center justify-center bg-gray-100 w-48 h-48 rounded-full',
                {hidden: Boolean(resumeData.avatar), flex: !resumeData.avatar},
              )}>
              <Icon
                name="person-outline"
                className="text-gray-400"
                size={125}
              />
            </div>
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-white rounded-xl flex items-center justify-center shadow-lg">
              {qrData ? (
                <QRCode data={qrData} {...qrConfig} />
              ) : (
                <Icon
                  name="qr-code-outline"
                  className="w-12 h-12 text-primary"
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <h1 className="text-4xl font-bold leading-none text-white drop-shadow-lg">
              {resumeData.firstName}{' '}
              <span className="font-extralight">{resumeData.lastName}</span>
            </h1>
            <p
              className={classNames(
                'text-md font-extralight leading-tight text-white',
                {'drop-shadow-md': !isForPDF},
              )}>
              {resumeData.title}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {resumeData.email && (
              <div className="flex items-center gap-1">
                <Icon name="mail-outline" className="w-4 h-4 text-white" />
                <span className="text-sm font-normal leading-tight text-white">
                  {resumeData.email}
                </span>
              </div>
            )}
            {resumeData.phone && (
              <div className="flex items-center gap-1">
                <Icon name="call-outline" className="w-4 h-4 text-white" />
                <span className="text-sm font-normal leading-tight text-white">
                  {resumeData.phone}
                </span>
              </div>
            )}
            {resumeData.location && (
              <div className="flex items-center gap-1">
                <Icon name="location-outline" className="w-4 h-4 text-white" />
                <span className="text-sm font-normal leading-tight text-white">
                  {resumeData.location}
                </span>
              </div>
            )}
            {showAvailability && (
              <div className="flex items-center gap-1">
                <Icon name="time-outline" className="w-4 h-4 text-white" />
                <span className="text-sm font-normal leading-tight text-white">
                  {availabilityInfo?.isAvailable
                    ? 'Beschikbaar'
                    : 'Niet beschikbaar'}
                  {availabilityInfo?.isAvailable && availabilityInfo?.hoursLabel
                    ? ` — ${availabilityInfo.hoursLabel} uur/week`
                    : ''}
                </span>
              </div>
            )}
          </div>
          {resumeData.bio && (
            <div className="bg-white/20 rounded-lg p-3 mt-2">
              <p className="text-sm font-light leading-tight text-white italic">
                &ldquo;{resumeData.bio}&rdquo;
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="px-10 pb-6">
        {/* Work Experience */}
        {resumeData.workExperience && resumeData.workExperience.length > 0 && (
          <div>
            <SectionHeader icon="briefcase-outline" title="Werkervaring" />

            {resumeData.workExperience.map((ctx, index) => (
              <ExperienceItem
                key={ctx.id || index}
                role={ctx.role || ''}
                company={ctx.name || ctx.company || undefined}
                startDate={ctx.startDate || undefined}
                endDate={ctx.endDate || undefined}
                media={ctx.media}
                skills={ctx.skills}
                summary={ctx.summary}
                onImageLoad={onImageLoad}
              />
            ))}
          </div>
        )}

        {/* Education */}
        {resumeData.education && (
          <div>
            <SectionHeader icon="school-outline" title="Opleidingen" />

            {resumeData.education.map((ctx, index) => (
              <EducationItem
                key={ctx.id || index}
                degree={ctx.role || ''}
                school={ctx.name || ctx.company || undefined}
                startDate={ctx.startDate || undefined}
                endDate={ctx.endDate || undefined}
                completed={
                  ctx.endDate ? new Date() > new Date(ctx.endDate) : false
                }
                media={ctx.media}
                skills={ctx.skills}
                summary={ctx.summary}
                onImageLoad={onImageLoad}
              />
            ))}
          </div>
        )}

        {/* Skills & Feedback */}
        {resumeData.skills && resumeData.skills.length > 0 && (
          <div className="space-y-3">
            <SectionHeader
              icon="star-outline"
              title="Vaardigheden & Waarderingen"
              showQRCode={true}
              qrText="Scan voor video's bij de skills"
            />
            <div className="grid grid-cols-2 gap-4">
              {resumeData.skills
                ?.slice(0, 6)
                .map((skill, index) => (
                  <SkillItem
                    key={index}
                    name={skill.skill?.name || ''}
                    rating={skill.rateAverage}
                    appreciatedByText={skill.appreciatedByText}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Portfolio */}
        {resumeData.portfolio && resumeData.portfolio.length > 0 && (
          <div className="space-y-3">
            <SectionHeader
              icon="images-outline"
              title="Visuele Portfolio - Slip Highlights"
              showQRCode={true}
            />
            <div className="grid grid-cols-2 gap-x-4">
              {resumeData.portfolio?.map((project, index) => (
                <PortfolioItem
                  key={index}
                  title={project.title}
                  description={project.description}
                  image={project.image}
                  tags={project.tags}
                  rating={project.rating}
                  index={index}
                  onImageLoad={onImageLoad}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-700 px-10 py-6 flex justify-between items-end">
        <div className="flex items-center gap-3 break-inside-avoid">
          <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center">
            {qrData ? (
              <QRCode
                data={qrData}
                width={70}
                height={70}
                margin={0}
                type="svg"
                backgroundOptions={{
                  color: 'transparent',
                }}
                imageOptions={{
                  hideBackgroundDots: true,
                  imageSize: 0.4,
                  margin: 0,
                }}
                dotsOptions={{
                  type: 'rounded' as const,
                  color: '#344054',
                }}
                cornersSquareOptions={{
                  type: 'extra-rounded' as const,
                  color: '#344054',
                }}
                cornersDotOptions={{
                  type: 'dot' as const,
                  color: '#344054',
                }}
              />
            ) : (
              <Icon name="qr-code-outline" className="w-8 h-8 text-gray-700" />
            )}
          </div>
          <div className="text-gray-100 ">
            <p className="text-sm font-semibold leading-tight">
              Scan deze QR Code en
              <br />
              Bekijk mijn volledige profiel &
              <br />
              aanbevelingen op het Slip Platform
            </p>
          </div>
        </div>
        <div className="text-right flex flex-col items-end justify-center h-20">
          <div className="text-gray-100 text-2xl font-bold">Slip</div>
          <p className="text-gray-400 text-xs font-normal leading-tight">
            Gegenereerd via Slip Platform
          </p>
        </div>
      </div>
    </div>
  );
}
